import { useState } from "react";

interface VerifiedEmployerModalProps {
  onClose: () => void;
  onVerified: () => void;
}

const steps = ["Company Info", "Verification", "Subscribe"];

export default function VerifiedEmployerModal({ onClose, onVerified }: VerifiedEmployerModalProps) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [form, setForm] = useState({
    companyName: "",
    ein: "",
    website: "",
    contactName: "",
    email: "",
    phone: "",
    state: "",
    industry: "",
    employees: "",
    docName: "",
    cardNumber: "",
    cardExpiry: "",
    cardCvc: "",
    cardName: "",
  });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setForm((f) => ({ ...f, docName: file.name }));
  };

  const handleSubscribe = () => {
    if (!agreed) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
    }, 2000);
  };

  const usStates = [
    "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
    "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
    "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
    "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
    "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
    "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
    "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-xl flex-shrink-0">
              <i className="ri-shield-check-line text-orange-500 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-gray-900">Become a Verified Employer</h2>
              <p className="text-xs text-gray-400 mt-0.5">Post real jobs · Hire young USA talent · $5/month</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-gray-600" />
          </button>
        </div>

        {done ? (
          <div className="p-12 text-center">
            <div className="w-24 h-24 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
              <i className="ri-shield-check-fill text-green-500 text-5xl" />
            </div>
            <h3 className="text-2xl font-extrabold text-gray-900 mb-2">You&apos;re Verified!</h3>
            <p className="text-sm text-gray-500 mb-1">
              <strong>{form.companyName || "Your company"}</strong> is now a Verified Employer on StarBookPrime.
            </p>
            <p className="text-xs text-gray-400 mb-2">Your verification badge is active. You can now post unlimited jobs and recruit young USA citizens.</p>
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-4 py-2 rounded-full mb-6">
              <i className="ri-shield-check-fill" /> Verified Employer · $5/month Active
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { onVerified(); onClose(); }}
                className="px-8 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-add-circle-line mr-1" /> Post Your First Job
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Step Indicator */}
            <div className="px-6 pt-5 pb-4">
              <div className="flex items-center gap-0">
                {steps.map((s, i) => (
                  <div key={s} className="flex items-center flex-1">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 transition-colors ${step >= i ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-400"}`}>{i + 1}</div>
                    {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-1 transition-colors ${step > i ? "bg-orange-500" : "bg-gray-100"}`} />}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2">
                {steps.map((s) => (
                  <span key={s} className="text-xs font-semibold text-gray-400">{s}</span>
                ))}
              </div>
            </div>

            {/* Why Verify Banner */}
            {step === 0 && (
              <div className="mx-6 mb-4 bg-orange-50 border border-orange-100 rounded-xl p-4">
                <p className="text-xs font-extrabold text-orange-800 mb-2"><i className="ri-information-line mr-1" />Why Verification Matters</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { icon: "ri-shield-check-fill", text: "All jobs are real & safe for young USA citizens" },
                    { icon: "ri-user-star-line", text: "Applicants trust verified employers 3x more" },
                    { icon: "ri-send-plane-line", text: "Applying & sending resumes is FREE for all job seekers" },
                  ].map((item) => (
                    <div key={item.text} className="flex items-start gap-2">
                      <i className={`${item.icon} text-orange-500 text-sm flex-shrink-0 mt-0.5`} />
                      <span className="text-xs text-orange-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-6 pb-6 space-y-4">
              {/* STEP 0: Company Info */}
              {step === 0 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Company / Factory Name *</label>
                      <input
                        value={form.companyName}
                        onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                        placeholder="e.g. TechVision Factory LLC"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">EIN (Tax ID) *</label>
                      <input
                        value={form.ein}
                        onChange={(e) => setForm((f) => ({ ...f, ein: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                        placeholder="XX-XXXXXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">State *</label>
                      <select
                        value={form.state}
                        onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
                      >
                        <option value="">Select state...</option>
                        {usStates.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Industry *</label>
                      <select
                        value={form.industry}
                        onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
                      >
                        <option value="">Select industry...</option>
                        <option>Manufacturing</option>
                        <option>Logistics & Warehousing</option>
                        <option>E-Commerce & Retail</option>
                        <option>Food & Beverage</option>
                        <option>Textiles & Apparel</option>
                        <option>Electronics</option>
                        <option>Construction & Trades</option>
                        <option>Healthcare</option>
                        <option>Technology</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Number of Employees</label>
                      <select
                        value={form.employees}
                        onChange={(e) => setForm((f) => ({ ...f, employees: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
                      >
                        <option value="">Select...</option>
                        <option>1-10</option>
                        <option>11-50</option>
                        <option>51-200</option>
                        <option>201-500</option>
                        <option>500+</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Company Website <span className="font-normal text-gray-400">(optional)</span></label>
                      <input
                        value={form.website}
                        onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                        placeholder="https://yourcompany.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Contact Name *</label>
                      <input
                        value={form.contactName}
                        onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Contact Phone *</label>
                      <input
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Business Email *</label>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                        placeholder="hr@yourcompany.com"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* STEP 1: Verification Documents */}
              {step === 1 && (
                <>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 mb-2">
                    <p className="text-xs font-extrabold text-gray-800 mb-1"><i className="ri-lock-line mr-1 text-orange-500" />Why We Verify</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      We verify every employer to ensure all jobs posted are <strong>real, safe, and fair</strong> for young USA citizens. This protects job seekers from scams and fake listings. Your documents are reviewed by our team within 24 hours.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">Upload Business License or EIN Letter *</label>
                    <label className={`flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${form.docName ? "border-green-400 bg-green-50" : "border-gray-200 hover:border-orange-300 bg-gray-50"}`}>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFileChange} />
                      {form.docName ? (
                        <>
                          <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full mb-2">
                            <i className="ri-file-check-line text-green-500 text-xl" />
                          </div>
                          <p className="text-sm font-bold text-green-600">{form.docName}</p>
                          <p className="text-xs text-gray-400 mt-1">Click to replace</p>
                        </>
                      ) : (
                        <>
                          <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-full mb-2">
                            <i className="ri-upload-cloud-2-line text-orange-500 text-xl" />
                          </div>
                          <p className="text-sm font-semibold text-gray-700">Upload document</p>
                          <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG — max 10MB</p>
                        </>
                      )}
                    </label>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    {[
                      { icon: "ri-file-text-line", title: "Accepted Documents", items: ["IRS EIN Confirmation Letter", "State Business License", "Articles of Incorporation", "DBA Certificate"] },
                      { icon: "ri-time-line", title: "Review Timeline", items: ["Documents reviewed within 24 hours", "Email confirmation sent to your business email", "Verified badge activated immediately after approval"] },
                    ].map((box) => (
                      <div key={box.title} className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                        <p className="text-xs font-extrabold text-orange-800 mb-2"><i className={`${box.icon} mr-1`} />{box.title}</p>
                        <ul className="space-y-1">
                          {box.items.map((item) => (
                            <li key={item} className="flex items-center gap-2 text-xs text-orange-700">
                              <i className="ri-check-line text-orange-500" />{item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* STEP 2: Subscribe $5/month */}
              {step === 2 && (
                <>
                  {/* Plan Card */}
                  <div className="border-2 border-orange-400 rounded-2xl p-5 bg-orange-50 relative overflow-hidden">
                    <div className="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Most Popular</div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-xl flex-shrink-0">
                        <i className="ri-shield-check-fill text-white text-2xl" />
                      </div>
                      <div>
                        <p className="font-extrabold text-gray-900 text-lg">Verified Employer Plan</p>
                        <p className="text-xs text-gray-500">For factories &amp; companies hiring USA talent</p>
                      </div>
                    </div>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-4xl font-extrabold text-gray-900">$5</span>
                      <span className="text-gray-500 text-sm">/month</span>
                      <span className="ml-2 text-xs bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">Cancel anytime</span>
                    </div>
                    <ul className="space-y-2.5 mb-4">
                      {[
                        { icon: "ri-shield-check-fill", text: "Verified Employer badge on all your job listings", color: "text-green-500" },
                        { icon: "ri-briefcase-4-line", text: "Post unlimited job openings", color: "text-orange-500" },
                        { icon: "ri-user-star-line", text: "Reach thousands of young USA citizens actively job hunting", color: "text-orange-500" },
                        { icon: "ri-message-3-line", text: "Direct messaging with applicants — no middlemen", color: "text-orange-500" },
                        { icon: "ri-bar-chart-line", text: "Applicant tracking & analytics dashboard", color: "text-orange-500" },
                        { icon: "ri-customer-service-2-line", text: "Priority support from our team", color: "text-orange-500" },
                      ].map((item) => (
                        <li key={item.text} className="flex items-start gap-2.5 text-sm text-gray-700">
                          <i className={`${item.icon} ${item.color} flex-shrink-0 mt-0.5`} />
                          {item.text}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-white rounded-xl p-3 border border-orange-200 text-center">
                      <p className="text-xs font-bold text-orange-700"><i className="ri-gift-line mr-1" />Job seekers apply &amp; send resumes 100% FREE — always</p>
                    </div>
                  </div>

                  {/* Payment */}
                  <div>
                    <p className="text-xs font-extrabold text-gray-700 mb-3"><i className="ri-bank-card-line mr-1 text-orange-500" />Payment Details</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-1.5">Cardholder Name</label>
                        <input
                          value={form.cardName}
                          onChange={(e) => setForm((f) => ({ ...f, cardName: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                          placeholder="Name on card"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-1.5">Card Number</label>
                        <div className="relative">
                          <input
                            value={form.cardNumber}
                            onChange={(e) => setForm((f) => ({ ...f, cardNumber: e.target.value }))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 pr-24"
                            placeholder="1234 5678 9012 3456"
                            maxLength={19}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            <span className="text-xs font-bold text-gray-400">VISA</span>
                            <span className="text-xs font-bold text-gray-400">MC</span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1.5">Expiry Date</label>
                          <input
                            value={form.cardExpiry}
                            onChange={(e) => setForm((f) => ({ ...f, cardExpiry: e.target.value }))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                            placeholder="MM / YY"
                            maxLength={7}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1.5">CVC</label>
                          <input
                            value={form.cardCvc}
                            onChange={(e) => setForm((f) => ({ ...f, cardCvc: e.target.value }))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                            placeholder="123"
                            maxLength={4}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Agreement */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-orange-500 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-xs text-gray-500 leading-relaxed">
                      I agree to the <span className="text-orange-500 font-semibold underline cursor-pointer">Terms of Service</span> and confirm that all jobs I post will be real, legal, and fair for young USA citizens. I understand that fake or misleading job listings will result in immediate account termination without refund.
                    </span>
                  </label>

                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <i className="ri-lock-2-line text-green-500" />
                    <span>Secured by 256-bit SSL encryption · Cancel anytime from your dashboard</span>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 sticky bottom-0 bg-white">
              {step > 0 && (
                <button
                  onClick={() => setStep((s) => (s - 1) as 0 | 1 | 2)}
                  className="px-6 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Back
                </button>
              )}
              {step < 2 ? (
                <button
                  onClick={() => setStep((s) => (s + 1) as 0 | 1 | 2)}
                  disabled={
                    (step === 0 && (!form.companyName || !form.ein || !form.state || !form.contactName || !form.email)) ||
                    (step === 1 && !form.docName)
                  }
                  className="flex-1 py-3 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue <i className="ri-arrow-right-line" />
                </button>
              ) : (
                <button
                  onClick={handleSubscribe}
                  disabled={!agreed || submitting || !form.cardName || !form.cardNumber}
                  className="flex-1 py-3 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin" /> Processing...</>
                  ) : (
                    <><i className="ri-shield-check-line" /> Activate Verified Plan — $5/mo</>
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
